import { AxiosInstance } from "axios"
import { safeContentDigest, syncHttpReq, SyncHttpReqResultStatus } from "@fusion-advantage/formrender"
import { DeviceToCloudBlobType, selectDeviceToCloudBlob, updateDeviceToCloudBlobSynced } from "../../dal/ldbDeviceToCloudBlob"
import { RImport, safeParseJson, safeUnparseJson, serializeFusionError } from '@fusion-advantage/backcompat'
import { REncounterCodeNextUpdate, SyncItemStatus } from "@fusion-advantage/formlib"
import { DeviceToCloudSyncInfo, DeviceToCloudSyncItemOk, encounterCodeNextUpdateToEncounterStatusBody, ZDeviceToCloudUploadFileBody } from "./ZDeviceToCloud"
import { err, ok } from "neverthrow"
import { SubtaskTicker } from "./syncThunk"
import { SyncItemResult, SyncItemSubtaskType } from "./SyncItemResult"
import fp from 'lodash/fp'

export const ERR_EXPECTED_RECORD = 'expected record but null'

export const deviceToCloudSyncItem = async (ticker: SubtaskTicker, axInst: AxiosInstance, ordinal: number, getSyncInfo: () => DeviceToCloudSyncInfo): Promise<SyncItemResult<DeviceToCloudSyncItemOk>> => {
	const rSelect = await selectDeviceToCloudBlob(ordinal)
	ticker(SyncItemSubtaskType.LdbRead1)
	if(rSelect.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncItem', rSelect.error),
		})
	}
	if(!rSelect.value) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncItem', ERR_EXPECTED_RECORD),
		})
	}
	let url: string
	let importBody: object
	let okValue: DeviceToCloudSyncItemOk = null
	switch(rSelect.value.type) {
		case DeviceToCloudBlobType.EncounterStatus:
			{
				url = '/api/v2/encounter/code-next'
				const rParse = safeParseJson(rSelect.value.data, REncounterCodeNextUpdate)
				ticker(SyncItemSubtaskType.Parse)
				if(rParse.isErr()) {
					return err({
						status: SyncItemStatus.Unknown,
						error: serializeFusionError('deviceToCloudSyncItem', rParse.error),
					})
				}
				importBody = encounterCodeNextUpdateToEncounterStatusBody(rParse.value)
				okValue = {
					encounterGuid: rParse.value.EncounterGUID,
					nextCodeNext: rParse.value.CodeNext,
				}
			}
			break
		case DeviceToCloudBlobType.Import:
			{
				url = '/api/v3/form/import'
				const rParse = safeParseJson(rSelect.value.data, RImport)
				ticker(SyncItemSubtaskType.Parse)
				if(rParse.isErr()) {
					return err({
						status: SyncItemStatus.Unknown,
						error: serializeFusionError('deviceToCloudSyncItem', rParse.error),
					})
				}
				importBody = rParse.value
			}
			break
		default:
			return err({
				status: SyncItemStatus.Unknown,
				error: serializeFusionError('deviceToCloudSyncItem', `blob type ${rSelect.value.type} not supported by this method`),
			})
	}
	let patched: object
	try {
		patched = fp.set('FormResponse.SyncInfo', getSyncInfo(), importBody)
	} catch(ex) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncItem', ex),
		})
	}
	const rUnparse = safeUnparseJson(patched)
	if(rUnparse.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncItem', rUnparse.error),
		})
	}
	const rDigest = safeContentDigest(rUnparse.value, true)
	if(rDigest.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncItem', rDigest.error),
		})
	}
	const resp = await syncHttpReq({
		axInst,
		method: 'POST',
		url,
		data: rUnparse.value,
		headers: {
			'Content-Digest': rDigest.value,
		},
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(resp.isErr()) {
		switch(resp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('deviceToCloudSyncItem', resp.error.error),
				})
		}
	}
	// dan keeps raw records of all successful imports
	// so he says we can toss these right away from ldb—
	// however, we do need to keep them on hand for the dashboard
	// until we've re-downloaded all work items, at which point the
	// imported data will come back to us
	const rUpdate = await updateDeviceToCloudBlobSynced(ordinal)
	ticker(SyncItemSubtaskType.LdbWrite1)
	if(rUpdate.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncItem', rUpdate.error),
		})
	}
	return ok({
		status: SyncItemStatus.UploadOk,
		value: okValue,
	})
}

export const deviceToCloudSyncEncounterFile = async (ticker: SubtaskTicker, axInst: AxiosInstance, plainAxInst: AxiosInstance, ordinal: number): Promise<SyncItemResult<null>> => {
	const rSelect = await selectDeviceToCloudBlob(ordinal)
	ticker(SyncItemSubtaskType.LdbRead1)
	if(rSelect.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncEncounterFile', rSelect.error),
		})
	}
	if(!rSelect.value) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncEncounterFile', ERR_EXPECTED_RECORD),
		})
	}
	if(rSelect.value.type !== DeviceToCloudBlobType.EncounterFile) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncEncounterFile', `expected blob type '${DeviceToCloudBlobType.EncounterFile}', got '${rSelect.value.type}'`)
		})
	}
	// filename is already put together with extension by insertEncounterFile
	const fileName = rSelect.value.fileId
	const rApi = await syncHttpReq({
		axInst,
		method: 'POST',
		url: '/api/v2/azure-blob/upload',
		data: {
			FileName: fileName,
		},
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(rApi.isErr()) {
		switch(rApi.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('deviceToCloudSyncEncounterFile', rApi.error.error),
				})
		}
	}
	const rApiParse = safeParseJson(rApi.value.data, ZDeviceToCloudUploadFileBody)
	ticker(SyncItemSubtaskType.Parse)
	if(rApiParse.isErr()) {
		return err({
			status: SyncItemStatus.InvalidBody,
			error: serializeFusionError('deviceToCloudSyncEncounterFile', rApiParse.error),
		})
	}
	let file: File
	try {
		file = new File([rSelect.value.data as BlobPart], fileName)
	} catch(ex) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncEncounterFile', ex),
		})
	}
	const rUpload = await syncHttpReq({
		axInst: plainAxInst,
		method: 'PUT',
		url: rApiParse.value.SasUrl,
		data: file,
		headers: {
			'Content-Type': rSelect.value.contentType ?? undefined,
			'x-ms-blob-type': 'BlockBlob',
		},
	})
	ticker(SyncItemSubtaskType.ApiRequest2)
	if(rUpload.isErr()) {
		switch(rUpload.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('deviceToCloudSyncEncounterFile', rUpload.error.error),
				})
		}
	}
	const rUpdate = await updateDeviceToCloudBlobSynced(rSelect.value.ordinal)
	ticker(SyncItemSubtaskType.LdbWrite1)
	if(rUpdate.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('deviceToCloudSyncEncounterFile', rUpdate.error),
		})
	}
	return ok({
		status: SyncItemStatus.UploadOk,
		value: null,
	})
}
